import { keywordsService } from '@/services/keywords.service';

const initialState = () => ({
  languages: [],
  status: {
    error: null,
    pending: true,
    resolved: false,
    rejected: false,
  },
});

const state = initialState();

const actions = {
  getKeywords({ commit }) {
    commit('GET_KEYWORDS');
    return keywordsService.getKeywords().then(
      (response) => {
        commit('SUCCESS', mapServerResponse(response));
      },
      (error) => commit('ERROR', error),
    );
  },
  refreshKeywords({ commit }) {
    commit('REFRESH_KEYWORDS');
    return keywordsService.getKeywords().then(
      (response) => commit('SUCCESS', mapServerResponse(response)),
      (error) => commit('ERROR', error),
    );
  },
  addCategory({ commit }, category) {
    commit('CRUD_KEYWORDS');
    return keywordsService.addCategory(category).then(
      (response) => commit('CRUD_KEYWORDS_SUCCESS'),
      (error) => {
        commit('ERROR', error);
        throw error;
      },
    );
  },
  deleteCategory({ commit }, categoryId) {
    commit('CRUD_KEYWORDS');
    return keywordsService.deleteCategory(categoryId).then(
      (response) => commit('CRUD_KEYWORDS_SUCCESS'),
      (error) => {
        commit('ERROR', error);
        throw error;
      },
    );
  },
  editCategory({ commit }, { categoryId, category }) {
    commit('CRUD_KEYWORDS');
    return keywordsService.editCategory({ categoryId, category }).then(
      (response) => commit('CRUD_KEYWORDS_SUCCESS'),
      (error) => {
        commit('ERROR', error);
        throw error;
      },
    );
  },
  editKeyword({ commit }, { keywordId, keyword }) {
    commit('CRUD_KEYWORDS');
    return keywordsService.editKeyword({ keywordId, keyword }).then(
      (response) => commit('CRUD_KEYWORDS_SUCCESS'),
      (error) => {
        commit('ERROR', error);
        throw error;
      },
    );
  },
  addKeywordsBulk({ commit }, keywords) {
    commit('CRUD_KEYWORDS');
    return keywordsService.addKeywordsBulk(keywords).then(
      (response) => commit('SUCCESS', mapServerResponse(response)),
      (error) => {
        commit('ERROR', error);
        throw error;
      },
    );
  },
  deleteKeywordsBulk({ commit }, ids) {
    commit('CRUD_KEYWORDS');
    return keywordsService.deleteKeywordsBulk(ids).then(
      (response) => commit('SUCCESS', mapServerResponse(response)),
      (error) => {
        commit('ERROR', error);
        throw error;
      },
    );
  },
};

const mutations = {
  GET_KEYWORDS(state) {
    state.status = {
      error: null,
      pending: true,
      resolved: false,
      rejected: false,
    };
    state.languages = [];
  },
  REFRESH_KEYWORDS(state) {
    state.status = {
      error: null,
      pending: true,
      resolved: false,
      rejected: false,
    };
  },
  CRUD_KEYWORDS(state) {
    state.status = {
      error: null,
      pending: true,
      resolved: false,
      rejected: false,
    };
  },
  CRUD_KEYWORDS_SUCCESS(state) {
    state.status = {
      ...state.status,
      pending: false,
      resolved: true,
    };
  },
  SUCCESS(state, languages) {
    state.status = {
      ...state.status,
      pending: false,
      resolved: true,
    };
    state.languages = languages;
  },
  ERROR(state, error) {
    state.status = {
      ...state.status,
      pending: false,
      rejected: true,
      error: error,
    };
  },
  RESET(state) {
    const initial = initialState();
    Object.keys(initial).forEach((key) => {
      state[key] = initial[key];
    });
  },
};

const getters = {
  languages: (state) => state.languages,
  categories: (state) => flattenCategories(state.languages),
  keywords: (state) => flattenKeywords(state.languages),
  keywordIds: (state) => mapToKeywordIds(state.languages),
  keywordCount: (state) => totalKeywordCount(state.languages),

  // status
  error: (state) => state.status.error,
  pending: (state) => state.status.pending,
  rejected: (state) => state.status.rejected,
  resolved: (state) => state.status.resolved,
};

export const keywords = {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};

// helper methods

// We map the `category > keyword` response to a `language > category > keyword` tree.
const mapServerResponse = (response) => {
  const languages = [...new Set(response.map((c) => c.language))];

  return languages.map((language) => {
    const categories = response.filter((category) => category.language === language);

    return {
      language,
      count: categories.length,
      keywordCount: keywordCount(categories),
      categories: categories.map(({ keywords, ...category }) => ({
        ...category,
        count: keywords.length,
        keywords: keywords.map((keyword) => ({
          language,
          ...keyword,
        })),
      })),
    };
  });
};

const flattenCategories = (languages = []) =>
  languages.reduce((categories, language) => categories.concat(language.categories), []);

const flattenKeywords = (languages = []) =>
  flattenCategories(languages).reduce((keywords, category) => keywords.concat(category.keywords), []);

const keywordCount = (categories = []) => categories.reduce((sum, category) => sum + category.keywords.length, 0);

const totalKeywordCount = (languages = []) => languages.reduce((sum, language) => sum + language.keywordCount, 0);

const mapToKeywordIds = (languages = []) => {
  return languages
    .map(({ language, categories }) => categories.map(({ id, name, keywords }) => keywords.map(({ id }) => id)))
    .flat(Infinity);
};
